import * as THREE from 'three';
import type { SfxName } from '../content/types';
import type { Controls } from '../core/device';
import { clamp, damp } from '../core/math';
import type { Random } from '../core/Random';
import type { Player } from '../entities/Player';
import { BullRideGame, RIDE_SECONDS, type BullKind } from '../minigames/bullride';
import type { MiniInput } from '../minigames/types';
import type { Vec2 } from '../world/layout';
import type { Stage, StageButton, StageHud, StageInput } from './types';

/** The bull you climb on, wherever it is. */
export interface BullMount {
  kind: BullKind;
  /** What bucks: rotated (and for the rodeo, moved) by the ride. */
  bull: THREE.Group;
  head?: THREE.Group;
  /** Height of its back, in the bull group's own space. The bull faces +x. */
  backY: number;
  /** The rodeo bull charges around the ring; the mechanical one spins in place. */
  arena?: { x: number; z: number; r: number };
  /** Where you end up standing afterwards (world). */
  exit: Vec2;
  /** Where the camera circles (world), and how far out. */
  orbit: { x: number; z: number; r: number; y: number };
  /** Called when the bull is (or isn't) being ridden. */
  setBusy(busy: boolean): void;
  /** Where the world parents the rider when they're not on the bull. */
  parent: THREE.Object3D;
}

const INTRO = 1.4;

/**
 * Bull riding, in the world: you're on the bull's back, it bucks and spins under you
 * and you throw your weight against it. Fall too far and you go flying.
 */
export class BullStage implements Stage {
  readonly title: string;
  readonly crosshair = false;
  readonly pointerLock = false;
  readonly game: BullRideGame;
  private t = 0;
  private phase: 'intro' | 'ride' | 'off' = 'intro';
  private offAt = 0;
  private readonly home = { pos: new THREE.Vector3(), rot: new THREE.Euler() };
  private wander = 0;
  private flying: { pos: THREE.Vector3; vel: THREE.Vector3; spin: number; landed: boolean } | null = null;
  private readonly tmp = new THREE.Vector3();
  private readonly look = new THREE.Vector3();
  private orbitA: number;
  private shake = 0;

  constructor(
    private readonly mount: BullMount,
    private readonly player: Player,
    private readonly rng: Random,
    private readonly sfx: (n: SfxName) => void,
    buzz: number,
  ) {
    this.game = new BullRideGame(rng, mount.kind, buzz);
    this.title = this.game.title;
    // Start from the crowd's side: the bleachers at the rodeo, the bar floor at the Spur.
    this.orbitA = mount.arena ? Math.PI * 0.35 : Math.PI * 0.6;
  }

  get done(): boolean {
    return this.phase === 'off' && this.t - this.offAt > 2.2;
  }

  hint(controls: Controls): string {
    if (this.phase === 'intro') return 'Grab the rope...';
    return this.game.hint(controls);
  }

  buttons(): StageButton[] {
    return [
      { code: 'ArrowRight', label: ':arrow-right:' },
      { code: 'ArrowLeft', label: ':arrow-left:' },
    ];
  }

  begin(): void {
    const { mount, player } = this;
    this.home.pos.copy(mount.bull.position);
    this.home.rot.copy(mount.bull.rotation);
    mount.setBusy(true);
    player.scripted = true;
    const rig = player.rig.root;
    mount.bull.add(rig);
    rig.position.set(0, mount.backY - 0.11, 0);
    rig.rotation.set(0, Math.PI / 2, 0);
    player.anim.update({ dt: 1, time: 0, speed: 0, pose: 'ride', seat: 0 });
    this.syncPlayer();
  }

  update(dt: number, input: StageInput): void {
    this.t += dt;
    const g = this.game;
    if (this.phase === 'intro') {
      this.player.anim.update({ dt, time: this.t, speed: 0, pose: 'ride', seat: 0 });
      this.player.rig.body.rotation.z = 0;
      this.idleBull(dt);
      if (this.t >= INTRO) {
        this.phase = 'ride';
        this.sfx(this.mount.arena ? 'airhorn' : 'crowd');
      }
      this.syncPlayer();
      return;
    }
    if (this.phase === 'ride') {
      // The ride logic takes left/right as key presses; a tap on either half of the screen counts too.
      const keys = [...input.keys];
      if (input.tap) keys.push(input.tap.x < 0 ? 'ArrowLeft' : 'ArrowRight');
      const mini: MiniInput = { held: input.held, pressed: false, released: false, tap: null, keys };
      g.update(dt, mini);
      this.buckBull(dt);
      this.player.anim.update({ dt, time: this.t, speed: 0, pose: 'ride', seat: 0 });
      // Lean against the bull: hips are the pivot, the bull's left is your left.
      this.player.rig.body.rotation.z = -g.lean * 0.95;
      this.shake = Math.max(this.shake * Math.exp(-dt * 6), Math.abs(g.buck) * 0.05);
      if (g.over) {
        this.phase = 'off';
        this.offAt = this.t;
        this.throwRider(g.thrown);
        this.sfx(g.thrown ? 'thud' : 'airhorn');
        if (g.thrown) window.setTimeout(() => this.sfx('crowd'), 400);
      }
      this.syncPlayer();
      return;
    }
    // Off the bull: fly (or hop) clear, and the bull settles down.
    this.idleBull(dt);
    this.fly(dt);
  }

  /** Stands (or paws the dirt) in the chute before the gate opens. */
  private idleBull(dt: number): void {
    const b = this.mount.bull;
    b.rotation.z = damp(b.rotation.z, 0, 5, dt);
    b.rotation.x = damp(b.rotation.x, 0, 5, dt);
    b.position.y = damp(b.position.y, this.home.pos.y, 6, dt);
    if (this.mount.head) this.mount.head.rotation.z = Math.sin(this.t * 3) * 0.2;
  }

  private buckBull(dt: number): void {
    const { mount, game: g } = this;
    const b = mount.bull;
    const t = this.t - INTRO;
    const hop = Math.abs(Math.sin(t * 5.5)) * 0.22 * Math.min(1.6, g.anger);
    if (mount.arena) {
      // Out of the chute, then loops around the ring, spinning as it goes.
      const a = mount.arena;
      this.wander += dt * (0.55 + 0.15 * g.anger);
      const target = this.tmp.set(
        a.x + Math.cos(this.wander + Math.PI) * a.r,
        0,
        a.z + Math.sin(this.wander + Math.PI) * a.r * 0.7,
      );
      const k = Math.min(1, t / 1.2);
      b.position.x = damp(b.position.x, target.x, 2.2 * k + 0.6, dt);
      b.position.z = damp(b.position.z, target.z, 2.2 * k + 0.6, dt);
      b.position.y = this.home.pos.y + hop;
      const travel = Math.atan2(-(target.z - b.position.z), target.x - b.position.x);
      b.rotation.y = travel + g.bullYaw * 2.2;
    } else {
      b.position.y = this.home.pos.y + hop * 0.35;
      b.rotation.y = this.home.rot.y + Math.sin(t * 1.6 * g.anger) * 0.9 + g.bullYaw * 2;
    }
    // Nose up, nose down (the bull faces +x, so that's a turn about z), plus a roll.
    b.rotation.z = g.bullPitch * 1.3 + Math.sin(t * 9) * 0.07;
    b.rotation.x = Math.sin(t * 6.3 + 1) * 0.08 * g.anger;
    if (mount.head) mount.head.rotation.z = Math.sin(t * 8) * 0.3;
  }

  /** Pops you off the bull: thrown to the side you were leaning, or a clean dismount. */
  private throwRider(thrown: boolean): void {
    const { player, mount } = this;
    const rig = player.rig.root;
    const pos = rig.getWorldPosition(new THREE.Vector3());
    const facing = mount.bull.getWorldQuaternion(new THREE.Quaternion());
    // The bull's right side, in the world.
    const side = new THREE.Vector3(0, 0, 1).applyQuaternion(facing).setY(0).normalize();
    const dir = Math.sign(this.game.lean || 1);
    mount.parent.add(rig);
    rig.position.copy(pos);
    rig.rotation.set(0, Math.atan2(side.x, side.z) * 1 + (dir > 0 ? 0 : Math.PI), 0);
    player.rig.body.rotation.z = 0;
    const out = thrown ? 3.4 : 1.6;
    this.flying = {
      pos,
      vel: side.multiplyScalar(dir * out).setY(thrown ? 4.6 : 2.6),
      spin: thrown ? dir * 7 : 0,
      landed: false,
    };
    // The rodeo bull trots back toward the chute; the mechanical one winds down.
    if (mount.arena)
      this.wander = Math.atan2(this.home.pos.z - mount.arena.z, this.home.pos.x - mount.arena.x);
  }

  private fly(dt: number): void {
    const f = this.flying;
    const { player } = this;
    const rig = player.rig.root;
    if (!f) return;
    if (!f.landed) {
      f.vel.y -= 13 * dt;
      f.pos.addScaledVector(f.vel, dt);
      const floor = 0;
      if (f.pos.y <= floor) {
        f.pos.y = floor;
        f.landed = true;
        this.sfx('thud');
        this.shake = 0.12;
      }
      rig.position.copy(f.pos);
      player.rig.body.rotation.x += f.spin * dt;
      player.anim.update({ dt, time: this.t, speed: 0, pose: f.spin ? 'fallen' : 'stand' });
      if (f.spin) player.rig.body.rotation.x = (this.t - this.offAt) * f.spin;
    } else {
      player.anim.update({
        dt,
        time: this.t,
        speed: 0,
        pose: this.game.thrown ? 'fallen' : 'cheer',
      });
      rig.position.copy(f.pos);
    }
    player.position.set(f.pos.x, 0, f.pos.z);
    player.y = Math.max(0, f.pos.y);
    // The rodeo bull heads home.
    const b = this.mount.bull;
    if (this.mount.arena) {
      b.position.x = damp(b.position.x, this.home.pos.x, 0.9, dt);
      b.position.z = damp(b.position.z, this.home.pos.z, 0.9, dt);
    }
    b.rotation.y = damp(b.rotation.y, this.home.rot.y, 2, dt);
  }

  /** Keep the player's position (and what the network sends) on the bull. */
  private syncPlayer(): void {
    const p = this.player.rig.root.getWorldPosition(this.tmp);
    this.player.position.set(p.x, 0, p.z);
    this.player.y = Math.max(0, p.y);
  }

  frame(camera: THREE.PerspectiveCamera, dt: number): void {
    const o = this.mount.orbit;
    // The mechanical bull sits by the back wall, so the camera sweeps the open side only.
    if (this.mount.arena) this.orbitA += dt * (this.phase === 'ride' ? 0.32 : 0.12);
    else this.orbitA = Math.PI * 0.6 + Math.sin(this.t * 0.35) * 0.5;
    const focus = this.player.rig.root.getWorldPosition(this.look);
    focus.y += this.phase === 'off' && this.game.thrown ? 0.4 : 1.3;
    // Follow the bull around the ring, from a safe distance outside the fence.
    const cx = this.mount.arena ? (o.x + focus.x) / 2 : o.x;
    const cz = this.mount.arena ? (o.z + focus.z) / 2 : o.z;
    const s = this.shake;
    camera.position.set(
      cx + Math.cos(this.orbitA) * o.r + (Math.random() - 0.5) * s,
      o.y + (Math.random() - 0.5) * s,
      cz + Math.sin(this.orbitA) * o.r,
    );
    camera.lookAt(focus);
  }

  hud(): StageHud {
    const g = this.game;
    const intro = this.phase === 'intro';
    const count = Math.ceil(INTRO - this.t);
    let banner: StageHud['banner'] = null;
    if (intro) banner = { text: count >= 1 ? `${count}...` : 'GO!', color: '#ffffff' };
    else if (this.phase === 'off')
      banner = g.thrown
        ? { text: 'EATIN’ DIRT!', color: '#ff8a8a' }
        : { text: 'QUALIFIED RIDE!', color: '#9dff6a' };
    return {
      score: `${g.time.toFixed(1)}s`,
      status: intro ? 'Nod when you’re ready, cowboy' : `Hang on ${RIDE_SECONDS} seconds`,
      meters: [
        {
          label: ':balance:',
          value: clamp((g.lean + 1) / 2, 0, 1),
          zone: [0.35, 0.65],
          marker: true,
          color: Math.abs(g.lean) > 0.7 ? '#ff4b4b' : '#ffd400',
        },
      ],
      banner,
    };
  }

  end(): void {
    const { mount, player } = this;
    mount.parent.add(player.rig.root);
    mount.bull.position.copy(this.home.pos);
    mount.bull.rotation.copy(this.home.rot);
    if (mount.head) mount.head.rotation.set(0, 0, 0);
    mount.setBusy(false);
    player.rig.body.rotation.set(0, 0, 0);
    player.rig.root.rotation.set(0, 0, 0);
    player.scripted = false;
    player.teleport(mount.exit.x, mount.exit.z, player.facing);
    void this.rng;
  }
}
